import 'client-only';

import type { TraceHeaders, Tracer } from './index';
import {
  buildTraceparent,
  buildTracestate,
  formatTraceId,
  randomHex,
  SESSION_ID_LENGTH,
  setTraceHeaderProvider,
  SPAN_ID_LENGTH,
  TRACE_COOKIE_DURATION_SEC,
  TRACE_COOKIE_NAME,
} from './index';

export class BrowserTracer implements Tracer {
  private sessionId = this.makeID();

  public init() {
    const priorSessionId = this.getSessionId();
    if (priorSessionId) {
      this.sessionId = priorSessionId;
    }
    this.persistSessionId();
    // Emit a W3C traceparent on outbound fetch/XHR (via the shared ky client's beforeRequest hook).
    setTraceHeaderProvider(() => this.buildHeaders());

    return this;
  }

  public buildHeaders(): TraceHeaders {
    const flags = window.location.search.match(/[?&]debug([^\w]|$)/i) ? '01' : '00';
    return {
      traceparent: buildTraceparent(this.makeTraceID(), this.makeID(SPAN_ID_LENGTH), flags),
      // Marks the trace as JS-originated so datarouter can attribute regenerations (see JSINFRA-3440).
      tracestate: buildTracestate({ js: '1' }),
    };
  }

  public makeTraceID() {
    const perfTime =
      typeof window !== 'undefined' && window.performance?.timeOrigin
        ? window.performance.now() + window.performance.timeOrigin // includes microsecond digits
        : Date.now();
    // Clamp to the wall clock: performance is monotonic, so a backward clock adjustment can push its
    // sum ahead of "now" and produce a future-dated timestamp that Java would reject and regenerate.
    const currentTimeMs = Math.min(perfTime, Date.now());
    return formatTraceId(BigInt(Math.round(currentTimeMs * 1_000_000)), this.makeID(SPAN_ID_LENGTH));
  }

  // Reads the prior sessionId from the rjs-trace cookie, tolerating the legacy
  // `sessionId:traceId:flag` shape by taking the first field.
  private getSessionId() {
    const prefix = `${TRACE_COOKIE_NAME}=`;
    const row = document.cookie.split('; ').find((cookie) => cookie.startsWith(prefix)) || '';
    return row.replace(prefix, '').split(':')[0];
  }

  private persistSessionId() {
    document.cookie = `${TRACE_COOKIE_NAME}=${this.sessionId}; path=/; max-age=${TRACE_COOKIE_DURATION_SEC}; samesite=strict`;
  }

  private makeID(length = SESSION_ID_LENGTH) {
    return randomHex(length);
  }
}
